import axiosInstance from '@/services/axiosInstance';
import {
  ExtendedUserResponseSchema,
  LoginHistoryView,
  RoleResponseSchema,
  UserGetAllParams,
  UserGetAllResponse,
  UserInviteRequestSchema,
  UserInviteResponse,
  UserLoginHistoryResponse,
  UserToggleActivationRequest,
  UserToggleResponse,
  UserUpdateRequestSchema,
} from '@/types/user.types';
import type { UserChangePasswordRequest } from '@/types/auth.types';
import { useInfiniteQuery, useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { isAdminLikeRole } from '@/lib/roles';
import { request } from '@/services/request';
import type { GenericErrorResponse, GenericErrorResponseError } from '@/types/api.types';

export const getAllUsers = (params?: UserGetAllParams): Promise<UserGetAllResponse> =>
  request(() => axiosInstance.get<UserGetAllResponse>('/users/', { params }));

export const useUsers = (
  params?: UserGetAllParams,
  options?: { query?: Partial<UseQueryOptions<UserGetAllResponse, GenericErrorResponse>> }
) => {
  const { queryKey, ...queryOptions } = options?.query ?? {};
  return useQuery<UserGetAllResponse, GenericErrorResponse>({
    ...queryOptions,
    queryKey: ['users', ...(queryKey ?? [params])],
    queryFn: () => getAllUsers(params),
  });
};

export const useRoles = () =>
  useQuery<RoleResponseSchema[], GenericErrorResponse>({
    queryKey: ['roles'],
    queryFn: () => request(() => axiosInstance.get<RoleResponseSchema[]>('/roles/')),
  });

export const useToggleUserActivation = () =>
  useMutation<UserToggleResponse, GenericErrorResponse, { id: string; data: UserToggleActivationRequest }>({
    mutationFn: ({ id, data }) =>
      request(() =>
        axiosInstance.patch<UserToggleResponse>(`/users/${encodeURIComponent(id)}/toggle-activation`, data)
      ),
  });

export const useChangePassword = () =>
  useMutation<{ message?: string }, GenericErrorResponse, { data: UserChangePasswordRequest }>({
    mutationFn: ({ data }) =>
      request(() => axiosInstance.patch<{ message?: string }>('/profile/change-password', data)),
  });

export const useInviteUser = () => {
  return useMutation<UserInviteResponse, GenericErrorResponse, { data: UserInviteRequestSchema }>({
    mutationFn: async ({ data }) => {
      const response = await axiosInstance.post<UserInviteResponse>(`/users/invite-user`, {
        ...data,
      });
      return response.data;
    },
  });
};

export const useGetUserById = (userId: string | undefined) => {
  return useQuery<ExtendedUserResponseSchema, GenericErrorResponseError>({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) throw new Error('userId is required');

      const response = await axiosInstance.get<ExtendedUserResponseSchema>(`/users/${userId}`);

      return response.data;
    },
    enabled: !!userId,
  });
};

export const useUpdateUser = () => {
  return useMutation<{ message: string }, GenericErrorResponse, { userId?: string; data: UserUpdateRequestSchema }>({
    mutationFn: async ({ userId, data }) => {
      if (!userId) throw new Error('userId is required');
      const response = await axiosInstance.put<{ message: string }>(`/users/${userId}`, {
        ...data,
      });
      return response.data;
    },
  });
};

export const isSupervisor = (user: ExtendedUserResponseSchema | null): boolean => {
  if (!user) return false;
  return user.teams?.some((team) => team.supervisorId === user.userId) ?? false;
};

export const isInTeam = (
  user: ExtendedUserResponseSchema | null,
  teamKey: string
): boolean => user?.teams?.some((team) => team.key === teamKey) ?? false;

export const isInAnyTeam = (
  user: ExtendedUserResponseSchema | null,
  teamKeys: string[]
): boolean => teamKeys.some((key) => isInTeam(user, key));

export const isBillingTeamMember = (user: ExtendedUserResponseSchema | null): boolean =>
  isInTeam(user, 'BILLING');

export const isAccountingTeamMember = (user: ExtendedUserResponseSchema | null): boolean =>
  isInTeam(user, 'ACCOUNTING');

export const isDueDiligenceTeamMember = (user: ExtendedUserResponseSchema | null): boolean =>
  isInTeam(user, 'DUE_DILIGENCE');

export const canAccessProcessBilling = (user: ExtendedUserResponseSchema | null): boolean => {
  if (!user) return false;
  if (user.roleId === 'ADMIN' || user.roleId === 'ACCOUNTANT') return true;
  if (isBillingTeamMember(user)) return true;
  if (isSupervisor(user)) return true;
  return false;
};

export const canAccessAccounting = (user: ExtendedUserResponseSchema | null): boolean => {
  if (!user) return false;
  if (user.roleId === 'ADMIN' || user.roleId === 'ACCOUNTANT') return true;
  if (isAccountingTeamMember(user)) return true;
  return false;
};

export const isBillingTeamSupervisor = (user: ExtendedUserResponseSchema | null): boolean => {
  if (!user) return false;
  return user.teams?.some((team) => team.key === 'BILLING' && team.supervisorId === user.userId) ?? false;
};

export const isAdminOrSupervisor = (user: ExtendedUserResponseSchema | null): boolean => {
  if (!user) return false;
  if (isAdminLikeRole(user.roleId)) return true;
  return isSupervisor(user);
};

/** Mirrors API assign-other: only admin/supervisor may pick an assignee on create/edit forms. */
export const canSelectDdiAssigneeOnForm = (user: ExtendedUserResponseSchema | null): boolean =>
  isAdminOrSupervisor(user);

/** Mirrors API self-assign: DDI team member, supervisor, or admin. */
export const canAssignDdiWorkOrderToMe = (user: ExtendedUserResponseSchema | null): boolean =>
  isDueDiligenceTeamMember(user) || isAdminOrSupervisor(user);

const LOGIN_HISTORY_PAGE_SIZE = 10;

const loadedRows = (page: UserLoginHistoryResponse) => (page.view === 'ALL' ? page.sessions.length : page.days.length);

// Admin-only log-in history for the user side panel (DGR-1835). Days in the
// daily summary follow the viewer's browser time zone.
export const useGetUserLoginHistory = (userId: string, view: LoginHistoryView) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return useInfiniteQuery<UserLoginHistoryResponse, GenericErrorResponseError>({
    queryKey: ['userLoginHistory', userId, view, timezone],
    queryFn: async ({ pageParam }) => {
      const response = await axiosInstance.get<UserLoginHistoryResponse>(`/users/${userId}/login-history`, {
        params: { view, page: pageParam, pageSize: LOGIN_HISTORY_PAGE_SIZE, timezone },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((total, page) => total + loadedRows(page), 0);
      return loaded < lastPage.totalRows ? pages.length + 1 : undefined;
    },
    enabled: !!userId,
  });
};
